/**
 * Guest demo sandbox — session-scoped flag for unauthenticated visitors
 * exploring Mock/Cached Workbench graphs and synthetic Fusion Canvas without SSO.
 *
 * Does not weaken backend REQUIRE_AUTH. Guests stay on /demo/* and are
 * blocked from live APIs by the axios allowlist in api.ts.
 */

export const GUEST_DEMO_KEY = 'cryptoflow-guest-demo';

export const GUEST_SIGN_IN_MESSAGE =
    'Sign in to use live blockchain tools, save cases, and run investigations.';

export const GUEST_LIVE_BLOCKED_MESSAGE =
    'Demo mode is Mock/Cached only. Sign in to switch to Live chain data.';

/** Paths (relative to API base /api/v1) guests may call. Static demo_cache.json is not axios. */
const GUEST_API_ALLOWLIST: RegExp[] = [
    /^\/?demo\/cached(\/|$)/i,
];

export function isGuestDemo(): boolean {
    try {
        return sessionStorage.getItem(GUEST_DEMO_KEY) === '1';
    } catch {
        return false;
    }
}

export function enterGuestDemo(): void {
    try {
        sessionStorage.setItem(GUEST_DEMO_KEY, '1');
    } catch {
        /* private mode */
    }
}

export function exitGuestDemo(): void {
    try {
        sessionStorage.removeItem(GUEST_DEMO_KEY);
    } catch {
        /* ignore */
    }
}

/** Mark guest session when landing on /demo without prior enter (shareable URL). */
export function ensureGuestDemo(): void {
    if (!isGuestDemo()) enterGuestDemo();
}

export function isGuestDemoPath(pathname: string): boolean {
    return pathname === '/demo' || pathname.startsWith('/demo/');
}

/**
 * True when this axios request URL is allowed for an unauthenticated guest.
 * `url` may be absolute or relative to API base.
 */
export function isGuestApiAllowed(url: string | undefined): boolean {
    if (!url) return false;
    let path = url;
    try {
        if (/^https?:\/\//i.test(url)) {
            path = new URL(url).pathname;
        }
    } catch {
        /* keep raw */
    }
    // Strip /api/v1 prefix if present
    path = path.replace(/^\/api\/v1/i, '');
    return GUEST_API_ALLOWLIST.some((re) => re.test(path));
}

export function guestWorkbenchBase(): string {
    return '/demo/workbench';
}

export function guestFusionBase(): string {
    return '/demo/fusion';
}

/** Full Fusion sandbox URL — guest uses /demo/fusion (no login). */
export function fusionHrefForDemo(
    demoId?: string,
    opts?: { guest?: boolean },
): string {
    const base = opts?.guest ? guestFusionBase() : '/fusion';
    if (!demoId) return base;
    return `${base}?demo=${encodeURIComponent(demoId)}`;
}

